import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { connectionsApi, proposalsApi } from '../utils/api';

// PRD-016 — simplified Proposal creation. The v2 Compose Surface (multi-step
// authoring orchestration) is removed; this is a minimal "name it + pick the
// Connection" form that creates the draft, then routes to the Proposal
// editor (ProposalDetail) to add Outcomes and sign.

function counterpartyName(conn) {
  // Tolerate a few response shapes across the connections API.
  return conn.counterparty?.name
    || conn.buyerWorkspace?.name || conn.sellerWorkspace?.name
    || conn.buyer_workspace_name || conn.seller_workspace_name
    || 'Counterparty';
}

export default function NewProposal() {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    connectionsApi.list()
      .then((res) => {
        const all = res.data.connections || res.data || [];
        const active = all.filter((c) => (c.state || c.status) === 'active');
        setConnections(active);
        if (active.length === 1) setConnectionId(active[0].id);
      })
      .catch(() => setError('Failed to load connections'))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !connectionId) return;
    setSaving(true);
    try {
      const res = await proposalsApi.create({ name: name.trim(), connectionId });
      navigate(`/proposals/${res.data.proposal.id}`);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to create proposal');
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto">
        <h1 className="page-header mb-1">New Proposal</h1>
        <p className="text-gray-600 mb-6">Name the deal and choose the Connection. You'll add Outcomes next.</p>

        {loading ? (
          <div className="card p-6 animate-pulse h-32" />
        ) : connections.length === 0 ? (
          <div className="card p-8 text-center">
            <p className="text-gray-600">You need an active Connection before drafting a Proposal.</p>
            <button className="btn-primary mt-4" onClick={() => navigate('/connections')}>Go to Connections</button>
          </div>
        ) : (
          <form onSubmit={submit} className="card p-6 space-y-4">
            <div>
              <label className="label">Proposal name *</label>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Q4 performance campaign" required />
            </div>
            <div>
              <label className="label">Connection (client) *</label>
              <select className="input" value={connectionId} onChange={(e) => setConnectionId(e.target.value)} required>
                <option value="">Select a Connection…</option>
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>{counterpartyName(c)}</option>
                ))}
              </select>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-3">
              <button type="button" className="btn-secondary" onClick={() => navigate('/proposals')}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving || !name.trim() || !connectionId}>
                {saving ? 'Creating…' : 'Create draft'}
              </button>
            </div>
          </form>
        )}
      </div>
    </Layout>
  );
}
